/**
 * SpringsLandingPage — shared template powering the 18 Colorado Springs
 * landing pages added in the local-ranking push (city×industry combos,
 * neighborhood pages, lead-magnet authority pages, proof/FAQ pages).
 *
 * Keeps each route file ~12 lines; all copy + schema lives in
 * src/lib/springsLandingData.ts.
 */
import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone, MapPin, CheckCircle2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SpringsCoverageMap } from "@/components/SpringsCoverageMap";
import { CityCoverageBlock } from "@/components/CityCoverageBlock";
import { RelatedPagesGrid, type RelatedPageCard } from "@/components/RelatedPagesGrid";
import {
  SCHEMA_LOCALBUSINESS_ID,
  SCHEMA_ORG_ID,
  SITE_URL,
} from "@/lib/entitySchema";
import { geoImageObject } from "@/lib/geoImageSchema";
import { ogImageFor, ogMetaTags } from "@/lib/ogImages";
import { getPageLastUpdated } from "@/lib/lastUpdatedRegistry";
import { LastUpdated } from "@/components/LastUpdated";

const SPRINGS_GEO = { lat: 38.8339, lng: -104.8214 };

const SPRINGS_NEIGHBORHOODS = [
  "Briargate", "Powers Corridor", "Old Colorado City", "Northgate",
  "Black Forest", "Patty Jewett", "Downtown", "Skyway",
];
const SPRINGS_ZIPS = [
  "80903","80904","80905","80906","80907","80908","80909","80910",
  "80911","80915","80916","80917","80918","80919","80920","80921",
  "80922","80923","80924","80925",
];
const SPRINGS_NEARBY = [
  "Monument","Fountain","Falcon","Manitou Springs","Woodland Park",
  "Security-Widefield","Peyton",
];

export type SpringsFAQ = { q: string; a: string };

export type SpringsLandingConfig = {
  slug: string;
  /** Page H1 + meta title. */
  title: string;
  /** Sub-eyebrow above the H1 in the hero. */
  eyebrow: string;
  /** Hero description / sub-headline (1–2 sentences). */
  heroDescription: string;
  /** Breadcrumb label on this page (e.g. "Briargate SEO"). */
  breadcrumbLabel: string;
  /** Meta description. */
  metaDescription: string;
  /** OG title (defaults to title). */
  ogTitle?: string;
  /** OG description (defaults to metaDescription). */
  ogDescription?: string;
  /** Lead paragraph rendered as the speakable intro. */
  intro: string;
  /** 2–4 body sections. */
  sections: { heading: string; body: string | string[]; bullets?: string[] }[];
  /** Optional list of ZIPs / neighborhoods this page targets (for schema). */
  zips?: string[];
  /** FAQ block + JSON-LD. */
  faqs: SpringsFAQ[];
  /** Cross-link cards in the footer. */
  related: RelatedPageCard[];
  /** Whether to render the SpringsCoverageMap section. */
  showCoverageMap?: boolean;
  /** Schema type for the JSON-LD WebPage (defaults to "WebPage"). */
  schemaType?: "WebPage" | "Article" | "FAQPage";
  /** Optional ISO date override for the "Last updated" stamp + schema dateModified. */
  lastUpdated?: string;
};

const CTA_PHONE = "+17194193935";
const CTA_DISPLAY = "(719) 419-3935";

export function buildSpringsLandingHead(config: SpringsLandingConfig) {
  const canonical = `${SITE_URL}/${config.slug}`;
  const ogTitle = config.ogTitle ?? config.title;
  const ogDescription = config.ogDescription ?? config.metaDescription;
  const zips = config.zips ?? [];
  const ogImage = `${SITE_URL}${ogImageFor({ city: "colorado-springs" })}`;
  const lastUpdated = config.lastUpdated ?? getPageLastUpdated(`/${config.slug}`);

  const imageNode = geoImageObject({
    url: ogImage,
    caption: `${config.eyebrow} in Colorado Springs, CO${zips.slice(0, 2).length ? ` (${zips.slice(0, 2).join(", ")})` : ""} — Colorado Web Impressions`,
    description: `${config.eyebrow} for Colorado Springs businesses${zips.length ? ` serving ZIPs ${zips.slice(0, 4).join(", ")}` : ""}. Owner-led local marketing by Colorado Web Impressions.`,
    cityName: "Colorado Springs",
    zips,
    geo: SPRINGS_GEO,
    width: 1216,
    height: 640,
  });

  const graph: Record<string, unknown>[] = [
    {
      "@type": "BreadcrumbList",
      "@id": `${canonical}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Colorado Springs",
          item: `${SITE_URL}/colorado-springs`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: config.breadcrumbLabel,
          item: canonical,
        },
      ],
    },
    {
      "@type": config.schemaType ?? "WebPage",
      "@id": `${canonical}#webpage`,
      url: canonical,
      name: config.title,
      description: config.metaDescription,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": SCHEMA_LOCALBUSINESS_ID },
      publisher: { "@id": SCHEMA_ORG_ID },
      primaryImageOfPage: imageNode,
      dateModified: lastUpdated,
    },
    {
      "@type": "Service",
      "@id": `${canonical}#service`,
      name: config.title,
      description: config.metaDescription,
      url: canonical,
      serviceType: config.eyebrow,
      provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
      areaServed: [
        { "@type": "City", name: "Colorado Springs" },
        { "@type": "State", name: "Colorado" },
      ],
      dateModified: lastUpdated,
    },
  ];


  if (config.faqs.length > 0) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${canonical}#faq`,
      mainEntity: config.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  if (config.zips && config.zips.length > 0) {
    for (const zip of config.zips) {
      graph.push({
        "@type": "PostalCodeArea",
        postalCode: zip,
        addressCountry: "US",
      });
    }
  }

  return {
    meta: [
      { title: config.title },
      { name: "description", content: config.metaDescription },
      { property: "og:title", content: ogTitle },
      { property: "og:description", content: ogDescription },
      { property: "og:url", content: canonical },
      { property: "og:type", content: "website" },
      ...ogMetaTags(ogImage),
      { name: "twitter:title", content: ogTitle },
      { name: "twitter:description", content: ogDescription },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": graph,
        }),
      },
    ],
  };
}

export function SpringsLandingPage({ config }: { config: SpringsLandingConfig }) {
  return (
    <SiteLayout>
      <PageHero
        eyebrow={config.eyebrow}
        title={config.title}
        description={config.heroDescription}
      >
        <div className="mt-6 flex justify-center">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Colorado Springs", to: "/colorado-springs" },
              { label: config.breadcrumbLabel },
            ]}
          />
        </div>
      </PageHero>

      <section className="bg-background py-14 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <LastUpdated date={config.lastUpdated ?? getPageLastUpdated(`/${config.slug}`)} className="mb-4" />
          <p className="speakable-intro text-lg leading-relaxed text-foreground/85">
            {config.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`tel:${CTA_PHONE}`}
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-5 py-3 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" /> Call Chris — {CTA_DISPLAY}
            </a>
            <Link
              to="/colorado-springs"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-5 py-3 text-sm font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
            >
              <MapPin className="h-4 w-4" /> Colorado Springs hub
            </Link>
          </div>
        </div>
      </section>

      {config.sections.map((s, i) => (
        <section
          key={i}
          className={
            i % 2 === 0
              ? "border-y border-border bg-muted/30 py-14 lg:py-16"
              : "bg-background py-14 lg:py-16"
          }
        >
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
              {s.heading}
            </h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground/85">
              {(Array.isArray(s.body) ? s.body : [s.body]).map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </div>
            {s.bullets && s.bullets.length > 0 && (
              <ul className="mt-6 space-y-3">
                {s.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-3 text-foreground/85">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-deep" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      ))}

      {config.showCoverageMap && <SpringsCoverageMap />}

      <CityCoverageBlock
        cityName="Colorado Springs"
        cityState="CO"
        neighborhoods={SPRINGS_NEIGHBORHOODS}
        zips={config.zips ?? SPRINGS_ZIPS}
        nearbyCities={SPRINGS_NEARBY}
      />


      {config.faqs.length > 0 && (
        <section className="bg-background py-16 lg:py-20">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="mt-8">
              {config.faqs.map((f, i) => (
                <AccordionItem key={i} value={`q-${i}`}>
                  <AccordionTrigger className="text-left text-base font-bold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer text-base leading-relaxed text-foreground/85">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {config.related.length > 0 && <RelatedPagesGrid cards={config.related} />}

      <section className="bg-gradient-dark py-16 text-center text-brand-cream lg:py-20">
        <div className="mx-auto max-w-2xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold sm:text-4xl">
            Talk to the owner — not a sales team
          </h2>
          <p className="mt-4 text-lg text-brand-cream/85">
            Flat pricing, owner-led, headquartered on Woodmen Rd. No handoffs,
            no nickel-and-diming, no offshore team.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={`tel:${CTA_PHONE}`}
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
             aria-label="Call (719) 419-3935">
              <Phone className="h-5 w-5" /> {CTA_DISPLAY}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md border-2 border-brand-cream/30 px-6 py-3 text-base font-bold text-brand-cream transition-smooth hover:border-brand-gold hover:text-brand-gold"
            >
              Request A Quote / Proposal <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
